/**
 * Tutorial engine — UI-independent guided-lesson state machine.
 *
 * Wraps the core engine (src/core/index.js) and walks the player through
 * the data-driven curriculum in lessons.js. The UI layer only needs to:
 *   1. render `engine.state` exactly like a normal game state,
 *   2. show `engine.step` (instruction / highlight),
 *   3. funnel the player's input Move into `engine.tryMove(move)`,
 *   4. offer a "skip lesson" button calling `engine.skipLesson()`,
 *   5. switch to the CPU-match invitation when `engine.isFinished`.
 *
 * Design rules (see the task contract):
 * - Never duplicates core rule logic: positions are staged via
 *   createGame(config) + a sequence of applyMove (all setup moves are
 *   themselves legal), and player input is validated with isLegal.
 * - Pure JS, browser compatible, fully deterministic (no randomness,
 *   scripted opponent replies embedded in the step data).
 *
 * tryMove(move) results:
 *   { result: 'advance', message?, done }
 *       正解。内部で applyMove し、スクリプトの相手手を自動で指し、
 *       次のステップへ進んだ。done は全レッスン完了かどうか。
 *   { result: 'wrong', message }
 *       合法だが期待と違う手（優しいヒント）、または不正手
 *       （ルール上できない手。レッスン固有の説明文があればそれを返す）。
 *       状態は一切進まない。
 *   { result: 'blocked', message }
 *       このステップでは許可されない操作種別（例: 移動ステップで壁）。
 *       状態は一切進まない。
 */

import { createGame, applyMove, isLegal } from '../core/index.js';
import { LESSONS } from './lessons.js';

export { LESSONS };

const TYPE_LABELS = {
  move: '駒の移動',
  wall: '壁の設置',
  ability: '能力の使用',
};

const DEFAULT_ILLEGAL_MESSAGE =
  'その手はルール上できません。光っているところを試してみましょう。';

function cellEq(a, b) {
  return !!a && !!b && a.col === b.col && a.row === b.row;
}

/** Structural equality of two Move objects (type-aware). */
function moveEquals(a, b) {
  if (!a || !b || a.type !== b.type) return false;
  if (a.type === 'move') return cellEq(a.to, b.to);
  if (a.type === 'wall') {
    return cellEq(a.at, b.at) && a.orientation === b.orientation;
  }
  if (a.type === 'ability') {
    if (a.ability !== b.ability) return false;
    if (a.to || b.to) return cellEq(a.to, b.to);
    return cellEq(a.at, b.at) && a.orientation === b.orientation;
  }
  if (a.type === 'pass') return true;
  return false;
}

/** Which Move.type values a step's expect accepts (for 'blocked' checks). */
export function allowedMoveTypes(expect) {
  switch (expect.kind) {
    case 'exact':
      return [expect.move.type];
    case 'oneOf':
      return [...new Set(expect.moves.map((m) => m.type))];
    case 'anyMove':
      return ['move'];
    case 'anyWall':
      return ['wall'];
    default:
      return [];
  }
}

/** Does a (already legal) move satisfy the step's expect? */
function matchesExpect(expect, move) {
  switch (expect.kind) {
    case 'exact':
      return moveEquals(expect.move, move);
    case 'oneOf':
      return expect.moves.some((m) => moveEquals(m, move));
    case 'anyMove':
      return move.type === 'move';
    case 'anyWall':
      return move.type === 'wall';
    default:
      return false;
  }
}

function defaultHint(expect) {
  const types = allowedMoveTypes(expect);
  if (types.includes('ability')) {
    return '能力を使って、光っているところを選んでみましょう。';
  }
  if (types.includes('wall')) {
    return '光っているミゾに壁を置いてみましょう。';
  }
  return '光っているマスをタップしてみましょう。';
}

function defaultBlockedMessage(expect) {
  const labels = allowedMoveTypes(expect)
    .map((t) => TYPE_LABELS[t] ?? t)
    .join('・');
  return `このステップでは「${labels}」を試してみましょう。`;
}

/**
 * Build a staged position: createGame(config) then apply the scripted
 * moves in order. Every move goes through the core's applyMove, so an
 * illegal setup move throws — the tests run all of them.
 */
function buildSetup(setup) {
  let state = createGame((setup && setup.config) || {});
  for (const move of (setup && setup.moves) || []) {
    state = applyMove(state, move);
  }
  return state;
}

/**
 * Create a tutorial engine. `lessons` defaults to the built-in curriculum;
 * tests may pass a custom lesson list.
 */
export function createTutorial(lessons = LESSONS) {
  if (!Array.isArray(lessons) || lessons.length === 0) {
    throw new Error('createTutorial: lessons must be a non-empty array');
  }

  let lessonIndex = 0;
  let stepIndex = 0;
  let finished = false;
  let state = null;
  let stepView = null;

  const lesson = () => lessons[lessonIndex];
  const stepDef = () => lesson().steps[stepIndex];

  /** (Re)compute state + the public step view when entering a step. */
  function enterStep(newLesson) {
    const l = lesson();
    const def = stepDef();
    if (newLesson && l.setup != null) state = buildSetup(l.setup);
    if (def.setup) state = buildSetup(def.setup);
    if (state === null) state = createGame();

    const highlight =
      typeof def.highlight === 'function'
        ? def.highlight(state)
        : (def.highlight ?? {});
    const instruction =
      typeof def.instruction === 'function'
        ? def.instruction(state)
        : def.instruction;

    stepView = {
      lessonId: l.id,
      lessonTitle: l.title,
      lessonIntro: l.intro ?? '',
      isLessonStart: stepIndex === 0,
      instruction,
      highlight,
      expect: def.expect,
      allowFreeCamera: def.allowFreeCamera ?? true,
    };

    // Graduation: a terminal no-input step at the very end of the course.
    if (
      def.expect.kind === 'none' &&
      lessonIndex === lessons.length - 1 &&
      stepIndex === l.steps.length - 1
    ) {
      finished = true;
    }
  }

  function advance() {
    stepIndex += 1;
    if (stepIndex < lesson().steps.length) {
      enterStep(false);
      return;
    }
    stepIndex = 0;
    lessonIndex += 1;
    if (lessonIndex >= lessons.length) {
      // Defensive: only reachable if the last lesson ends with an
      // actionable step (the built-in curriculum ends with 'none').
      lessonIndex = lessons.length - 1;
      stepIndex = lessons[lessonIndex].steps.length - 1;
      finished = true;
      enterStep(false);
      return;
    }
    enterStep(true);
  }

  enterStep(true);

  return {
    /** Current core game state — render it exactly like a normal game. */
    get state() {
      return state;
    },

    /** Current step info: { lessonTitle, instruction, highlight, ... } */
    get step() {
      return stepView;
    },

    get progress() {
      return {
        lessonIndex,
        stepIndex,
        lessonCount: lessons.length,
        stepCount: lesson().steps.length,
      };
    },

    /** True once the whole course (graduation screen) is reached. */
    get isFinished() {
      return finished;
    },

    /**
     * Validate a player's input move against the current step.
     * Never mutates state on 'wrong' / 'blocked'.
     */
    tryMove(move) {
      const def = stepDef();

      if (finished || def.expect.kind === 'none') {
        return {
          result: 'blocked',
          message: def.blockedMessage ?? stepView.instruction,
          done: finished,
        };
      }

      const allowed = allowedMoveTypes(def.expect);
      if (!move || typeof move !== 'object' || !allowed.includes(move.type)) {
        return {
          result: 'blocked',
          message: def.blockedMessage ?? defaultBlockedMessage(def.expect),
        };
      }

      if (!isLegal(state, move)) {
        // ルール違反（例: 道を完全にふさぐ壁）。レッスン固有の説明があれば
        // それを返す（レッスン4の「赤い壁」体験）。
        return {
          result: 'wrong',
          message: def.illegalMessage ?? DEFAULT_ILLEGAL_MESSAGE,
        };
      }

      if (!matchesExpect(def.expect, move)) {
        return {
          result: 'wrong',
          message: def.hint ?? defaultHint(def.expect),
        };
      }

      // Correct: apply the player's move, then the scripted opponent reply.
      const before = state;
      const after = applyMove(state, move);
      state = after;
      let opponentMove = null;
      if (def.opponent && state.winner === null) {
        opponentMove = def.opponent;
        state = applyMove(state, opponentMove);
      }

      const message =
        typeof def.success === 'function'
          ? def.success({ before, after, final: state, opponentMove })
          : (def.success ?? '');

      advance();
      return { result: 'advance', message, done: finished };
    },

    /** Skip the rest of the current lesson and jump to the next one. */
    skipLesson() {
      if (finished) return;
      if (lessonIndex >= lessons.length - 1) {
        // Skipping the final lesson ends the tutorial.
        finished = true;
        return;
      }
      lessonIndex += 1;
      stepIndex = 0;
      enterStep(true);
    },
  };
}
